import React, { useCallback, useEffect, useState } from "react";
import { useLazyGetSocialResponsibilitiesQuery } from "../../api/Certifications";
import { Certification } from "../../types/Certifications";
import "./ResponsabilitaSocialeView.css";
import { useMediaQuery } from "@mui/material";
import { bpTheme, useTheme } from "../../Providers/ThemeProvider";
import { useLazyGetWhistleblowingsQuery } from "../../api/Whistleblowing";
import { Whistleblowing } from "../../types/Whistleblowing";
import { TailSpin } from "react-loader-spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { Alert } from "react-bootstrap";
import { Col, Flex } from "antd";

export default function ResponsabilitaSocialeView() {
  const isSm = useMediaQuery(bpTheme.breakpoints.down("sm"));
  const [getSocialResponsibilities] = useLazyGetSocialResponsibilitiesQuery();
  const [getWhistleblowings] = useLazyGetWhistleblowingsQuery();
  const [whistleBlowing, setWhistleblowing] = useState<Whistleblowing>();
  const { theme } = useTheme();
  const [error, setError] = useState(false);
  const [whistleBlowingError, setWhistleBlowingError] = useState(false);
  const [responsibilities, setResponsibilities] = useState<Certification[]>();

  const initSocialResponsibilities = useCallback(() => {
    getSocialResponsibilities({}).then(
      ({ data }) => {
        if (data && data.list.length > 0) {
          setResponsibilities(data.list);
        }
      },
      () => {
        setError(true);
      }
    );
  }, [getSocialResponsibilities]);

  const initWhistleblowing = useCallback(() => {
    getWhistleblowings({}).then(
      ({ data }) => {
        if (data && data.list.length > 0) {
          setWhistleblowing(data.list[0]);
        }
      },
      () => {
        setWhistleBlowingError(true);
      }
    );
  }, [getWhistleblowings]);

  useEffect(() => {
    initSocialResponsibilities();
    initWhistleblowing();
  }, [initSocialResponsibilities, initWhistleblowing]);

  const handleDownload =
    (signedUrl: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const link = document.createElement("a");
      link.href = signedUrl;
      link.setAttribute("download", "file"); // Nome del file (opzionale, alcuni URL potrebbero non supportarlo)
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

  const renderResponsibilities = useCallback(() => {
    if (error) {
      return (
        <Alert variant="danger" className="py-2 mt-4 w-50 m-auto">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );
    }
    return responsibilities!.length === 0 ? (
      <Alert variant="info" className="py-2 mt-4 w-50 m-auto">
        <FontAwesomeIcon icon={faInfoCircle} />
        <span className="ms-2">Nessuna Responsabilità Sociale Presente</span>
      </Alert>
    ) : (
      <div>
        {responsibilities!.map((el, index) => (
          <div className="p-sm-5" key={index}>
            {!isSm ? (
              <React.Fragment>
                <Flex className="justify-content-end text-end">
                  <Col span={12} className="justify-content-start text-start">
                    <img
                      style={{
                        width: 160,
                        height: 120,
                      }}
                      src={el.signedImageUrl}
                      alt={el.title}
                    />
                  </Col>
                  <Col span={12} className="justify-content-end flex-end">
                    <p className="h2 text-uppercase text-end">{el.title}</p>
                    <hr className="hr-secondary py-1" />
                    {el.text && el.text !== "" && (
                      <span dangerouslySetInnerHTML={{ __html: el.text }} />
                    )}
                    <Flex className="gap-2 justify-content-end">
                      {(el?.signedFileUrls || []).map((file, index) => (
                        <a
                          key={index}
                          onClick={handleDownload(file)}
                          target="_blank"
                          rel="noopener"
                          className="my-btn my-btn-content"
                        >
                          Scarica
                        </a>
                      ))}
                    </Flex>
                  </Col>
                </Flex>
              </React.Fragment>
            ) : (
              <Col span={24} className="gap-3">
                <img
                  style={{
                    width: 60,
                    height: 50,
                    marginBottom: 12,
                    marginTop: 24,
                  }}
                  src={el.signedImageUrl}
                  alt={el.title}
                />
                <p className="h2 text-uppercase text-center">{el.title}</p>
                <span dangerouslySetInnerHTML={{ __html: el.text }} />
                <div
                  style={{
                    display: "flex",
                    alignSelf: "center",
                    flexDirection: "row",
                    justifyContent: "space-around",
                    marginTop: 32,
                    marginBottom: 32,
                  }}
                >
                  {(el?.signedFileUrls || []).map((file, index) => (
                    <span key={index}>
                      <a
                        onClick={handleDownload(file)}
                        target="_blank"
                        rel="noopener"
                        className="my-btn my-btn-content"
                      >
                        Scarica
                      </a>
                    </span>
                  ))}
                </div>
              </Col>
            )}
            {index < responsibilities!.length - 1 && (
              <hr className="hr-primary w-xs-75 w-sm-50 mt-4" />
            )}
          </div>
        ))}
      </div>
    );
  }, [responsibilities, error]);

  const renderResponsibilitiesWhistle = useCallback(() => {
    if (whistleBlowingError) {
      return (
        <Alert variant="danger" className="py-2 mt-4 w-50 m-auto">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );
    }
    return (
      <div
        style={{
          border: "2px solid #B7D97D",
          boxShadow: "0 4px 12px rgba(183, 217, 125, 0.4)",
        }}
        className="px-xs-5 p-sm-5 mt-5"
      >
        {!isSm ? (
          <React.Fragment>
            <Flex className="justify-content-end text-end">
              <Col span={12} className="justify-content-start text-start">
                <img
                  style={{
                    width: 160,
                    height: 120,
                  }}
                  src={whistleBlowing!.signedImageUrl}
                  alt={whistleBlowing!.title}
                />
              </Col>
              <Col span={12} className="justify-content-end flex-end">
                <p className="h2 text-uppercase text-end">
                  {whistleBlowing!.title}
                </p>
                <hr className="hr-secondary py-1" />
                {whistleBlowing!.text && whistleBlowing!.text !== "" && (
                  <span
                    dangerouslySetInnerHTML={{ __html: whistleBlowing!.text }}
                  />
                )}
                <div
                  style={{
                    display: "flex",
                    alignSelf: "center",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    marginTop: 32,
                  }}
                >
                  <span>
                    <a
                      href={whistleBlowing!.buttonLink}
                      target="_blank"
                      rel="noopener"
                      className="my-btn my-btn-content"
                    >
                      {whistleBlowing!.buttonLabel}
                    </a>
                  </span>
                </div>
              </Col>
            </Flex>
          </React.Fragment>
        ) : (
          <Col span={24} className="gap-3">
            <img
              style={{
                width: 60,
                height: 50,
                marginBottom: 12,
                marginTop: 24,
              }}
              src={whistleBlowing!.signedImageUrl}
              alt={whistleBlowing!.title}
            />
            <p className="h2 text-uppercase text-center">
              {whistleBlowing!.title}
            </p>
            <span dangerouslySetInnerHTML={{ __html: whistleBlowing!.text }} />
            <div
              style={{
                display: "flex",
                alignSelf: "center",
                flexDirection: "row",
                justifyContent: "space-around",
                marginTop: 32,
                marginBottom: 40,
              }}
            >
              <span>
                <a
                  href={whistleBlowing!.buttonLink}
                  target="_blank"
                  rel="noopener"
                  className="my-btn my-btn-content"
                >
                  {whistleBlowing!.buttonLabel}
                </a>
              </span>
            </div>
          </Col>
        )}
      </div>
    );
  }, [whistleBlowingError, whistleBlowing]);

  const renderAsyncContent = useCallback(() => {
    return responsibilities ? (
      renderResponsibilities()
    ) : (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin
          height={100}
          width={100}
          color={theme.colors.primary}
          radius="1"
        />
      </div>
    );
  }, [renderResponsibilities, responsibilities]);

  const renderAsyncContentWhistleBlowing = useCallback(() => {
    return whistleBlowing ? (
      renderResponsibilitiesWhistle()
    ) : (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin
          height={100}
          width={100}
          color={theme.colors.primary}
          radius="1"
        />
      </div>
    );
  }, [whistleBlowing, renderResponsibilitiesWhistle]);

  return (
    <div
      className="container justify-content-center text-center"
      style={{
        position: "relative",
        paddingTop: "12vh",
        paddingBottom: "8vh",
        minHeight: "60vh",
      }}
    >
      <img
        src={require("../../assets/img/logo.png")}
        style={{ width: 120, height: 80, marginBottom: 16 }}
        alt="Logo"
      />
      <h2 className="text-uppercase py-4">Responsabilità Sociale</h2>
      <hr className="hr-primary w-xs-75 w-sm-50" />
      {renderAsyncContent()}
      {renderAsyncContentWhistleBlowing()}
    </div>
  );
}
